import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharacterSummary, Outfit } from '../../../shared/src/protocol';
import { vocationTitle } from '../../../shared/src/game/vocations';
import { api } from '../api';
import { renderOutfit } from '../game/outfit';
import { Button, ErrorText, Window } from '../ui';

export function OutfitImage(props: { outfit: Outfit; scale?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let alive = true;
    renderOutfit(props.outfit).then((c) => {
      if (!alive || !ref.current) return;
      c.style.width = c.width * (props.scale ?? 1) + 'px';
      c.style.height = c.height * (props.scale ?? 1) + 'px';
      ref.current.replaceChildren(c);
    }).catch(() => {});
    return () => { alive = false; };
  }, [JSON.stringify(props.outfit)]);
  return <div class="outfit" ref={ref} />;
}

export function Characters(props: { onPlay: (c: CharacterSummary) => void; onCreate: () => void; onLogout: () => void; onExpired: () => void }) {
  const [list, setList] = useState<CharacterSummary[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [del, setDel] = useState<{ name: string; password: string; busy: boolean; error: string | null } | null>(null);
  const [acc, setAcc] = useState<{ email: string; hasPassword: boolean; google: boolean } | null>(null);
  const [pw, setPw] = useState<{ current: string; next: string; again: string; busy: boolean; msg: string | null; ok?: boolean } | null>(null);
  const openAccount = () => { setPw({ current: '', next: '', again: '', busy: false, msg: null }); api.account().then(setAcc).catch((e) => setPw((p) => p && { ...p, msg: e.message })); };
  const savePassword = async () => {
    if (!pw) return;
    if (pw.next !== pw.again) return setPw({ ...pw, msg: 'As senhas não conferem.' });
    setPw({ ...pw, busy: true, msg: null });
    try { await api.changePassword(pw.current, pw.next); setPw({ current: '', next: '', again: '', busy: false, msg: 'Senha salva.', ok: true }); setAcc(acc && { ...acc, hasPassword: true }); }
    catch (e: any) { setPw({ ...pw, busy: false, msg: e.message }); }
  };

  useEffect(() => {
    api.characters().then((l) => { setList(l); setSelected(l[0]?.id ?? null); })
      .catch((e) => (e.status === 401 ? props.onExpired() : setError(e.message)));
  }, []);

  const chosen = list?.find((c) => c.id === selected);
  const confirmDelete = async () => {
    if (!chosen || !del) return;
    setDel({ ...del, busy: true, error: null });
    try {
      await api.deleteCharacter(chosen.id, del.name, del.password);
      const l = list!.filter((c) => c.id !== chosen.id);
      setList(l); setSelected(l[0]?.id ?? null); setDel(null);
    } catch (e: any) {
      if (e.status === 401 && /sess/i.test(e.message)) return props.onExpired();
      setDel({ ...del, busy: false, error: e.message });
    }
  };

  return (
    <div class="screen center nx-bg">
      <img class="nx-logo small" src="/ui/logo.webp" alt="Baiak: Noxara" />
      <Window title="Escolha o personagem" width={480}>
        <ErrorText error={error} />
        {list === null && !error && <p class="muted">Carregando...</p>}
        {list?.length === 0 && <p class="muted">Você ainda não tem personagens. Crie o primeiro para começar.</p>}
        {!!list?.length && (
          <ul class="char-list" role="listbox">
            {list.map((c) => (
              <li
                key={c.id}
                role="option"
                aria-selected={c.id === selected}
                class={c.id === selected ? 'selected' : ''}
                onClick={() => setSelected(c.id)}
                onDblClick={() => props.onPlay(c)}
              >
                <OutfitImage outfit={c.outfit} />
                <div>
                  <strong>{c.name}</strong>
                  <span class="muted">Level {c.level} · {vocationTitle(c.vocation, c.promotion ?? 0)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div class="row between">
          <div class="row">
            <Button onClick={props.onCreate}>Criar personagem</Button>
            <Button onClick={openAccount}>Conta</Button>
            <Button onClick={props.onLogout}>Sair</Button>
            <Button disabled={!chosen} onClick={() => setDel({ name: '', password: '', busy: false, error: null })}>Apagar</Button>
          </div>
          <Button variant="green" disabled={!chosen} onClick={() => chosen && props.onPlay(chosen)}>Jogar</Button>
        </div>
      </Window>
      {pw && (
        <Window title="Conta" width={380}>
          {!acc ? <p class="muted">Carregando...</p> : (
            <>
              <p class="small">E-mail: <b>{acc.email}</b><br />Entrar com: {[acc.hasPassword && 'e-mail e senha', acc.google && 'Google'].filter(Boolean).join(' e ')}</p>
              {!acc.hasPassword && <p class="small muted">Sua conta entra só pelo Google. Crie uma senha para também entrar com e-mail e senha e para poder apagar personagens.</p>}
              {acc.hasPassword && (
                <label class="tb-field">Senha atual
                  <input type="password" autocomplete="current-password" value={pw.current} onInput={(e) => setPw({ ...pw, current: (e.target as HTMLInputElement).value })} />
                </label>
              )}
              <label class="tb-field">{acc.hasPassword ? 'Nova senha' : 'Senha'} (mínimo 8 caracteres)
                <input type="password" autocomplete="new-password" value={pw.next} onInput={(e) => setPw({ ...pw, next: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="tb-field">Repita a senha
                <input type="password" autocomplete="new-password" value={pw.again} onInput={(e) => setPw({ ...pw, again: (e.target as HTMLInputElement).value })} />
              </label>
              {pw.msg && <p class={'small ' + (pw.ok ? 'green' : 'error')}>{pw.msg}</p>}
            </>
          )}
          <div class="row between">
            <Button onClick={() => setPw(null)}>Fechar</Button>
            <Button variant="green" disabled={!acc || pw.busy || pw.next.length < 8} onClick={savePassword}>{acc?.hasPassword ? 'Trocar senha' : 'Criar senha'}</Button>
          </div>
        </Window>
      )}
      {del && chosen && (
        <Window title="Apagar personagem" width={380}>
          <p class="error"><b>Atenção:</b> apagar {chosen.name} é <b>permanente</b>. Level, skills, itens, depot, banco e progresso serão perdidos e não podem ser recuperados.</p>
          <label class="tb-field">Digite o nome do personagem para confirmar
            <input value={del.name} placeholder={chosen.name} onInput={(e) => setDel({ ...del, name: (e.target as HTMLInputElement).value })} />
          </label>
          <label class="tb-field">Senha da conta
            <input type="password" autocomplete="current-password" value={del.password} onInput={(e) => setDel({ ...del, password: (e.target as HTMLInputElement).value })} />
          </label>
          <ErrorText error={del.error} />
          <div class="row between">
            <Button onClick={() => setDel(null)}>Cancelar</Button>
            <Button variant="red" disabled={del.busy || del.name.trim().toLowerCase() !== chosen.name.toLowerCase() || del.password.length < 8} onClick={confirmDelete}>Apagar para sempre</Button>
          </div>
        </Window>
      )}
    </div>
  );
}
